import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import GavelIcon from '@mui/icons-material/Gavel';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import * as d3 from 'd3';
import { EventTooltipContent } from '../EventCard';
import { formatVolume } from '../../utils/format';
import { useContainerWidth } from '../../hooks/useContainerWidth';
import type { EventFromAsset } from '../../api/types';
import { useMultiMarketProbability } from '../../api/useMultiMarketProbability';
import type { MarketSeriesData } from '../../api/marketPricesClient';

type MarketType = 'price_level' | 'macro_release' | 'policy_decision' | 'geopolitical' | 'corporate';

interface LaneConfig { key: MarketType; label: string; shortLabel: string; color: string }

const LANE_CONFIG: LaneConfig[] = [
    { key: 'price_level', label: 'Price Level', shortLabel: 'PRICE', color: '#f59e0b' },
    { key: 'macro_release', label: 'Macro Release', shortLabel: 'MACRO', color: '#3b82f6' },
    { key: 'policy_decision', label: 'Policy Decision', shortLabel: 'POLICY', color: '#a855f7' },
    { key: 'geopolitical', label: 'Geopolitical', shortLabel: 'GEO', color: '#ef4444' },
    { key: 'corporate', label: 'Corporate', shortLabel: 'CORP', color: '#10b981' },
];

const MUI_ICONS: Record<MarketType, typeof TrendingUpIcon> = {
    price_level: TrendingUpIcon, macro_release: BarChartIcon,
    policy_decision: GavelIcon, geopolitical: PublicIcon, corporate: BusinessIcon,
};

const MARGIN_RIGHT = 48;
const ROW_GAP = 2;
const LANE_GAP = 6;
const LANE_PADDING_Y = 4;
const TIMELINE_HEIGHT = 22;
const DEFAULT_CHART_HEIGHT = 300;
const MIN_ROW_H = 14;

interface PlacedEvent { evt: EventFromAsset; row: number }

function assignRows(events: EventFromAsset[], xScale: d3.ScaleTime<number, number>): PlacedEvent[] {
    const sorted = [...events].sort((a, b) =>
        xScale(new Date(a.start_date)) - xScale(new Date(b.start_date))
    );
    const rowEnds: number[] = [];
    const placed: PlacedEvent[] = [];
    for (const evt of sorted) {
        const x1 = Math.max(xScale.range()[0], xScale(new Date(evt.start_date)));
        const x2 = xScale(new Date(evt.end_date));
        if (x2 <= x1 + 2) continue;
        let bestRow = -1;
        for (let r = 0; r < rowEnds.length; r++) {
            if (rowEnds[r] <= x1 + 2) { bestRow = r; break; }
        }
        if (bestRow === -1) { bestRow = rowEnds.length; rowEnds.push(0); }
        rowEnds[bestRow] = x2;
        placed.push({ evt, row: bestRow });
    }
    return placed;
}

function pickTopMarkets(markets: EventFromAsset['markets_summary']): EventFromAsset['markets_summary'] {
    if (markets.length <= 2) return [...markets].sort((a, b) => b.yes_prob - a.yes_prob);

    const byProb = [...markets].sort((a, b) => b.yes_prob - a.yes_prob);
    const byVol = [...markets].sort((a, b) => (b.volume || 0) - (a.volume || 0));

    const result: EventFromAsset['markets_summary'] = [];
    const seen = new Set<string>();

    // highest probability first
    result.push(byProb[0]);
    seen.add(byProb[0].condition_id);

    // rest by probability descending
    for (const m of byProb) {
        if (!seen.has(m.condition_id)) {
            result.push(m);
            seen.add(m.condition_id);
        }
    }
    return result;
}

function outcomeLabel(evt: EventFromAsset): { text: string; prob: number } | null {
    const lead = evt.markets_summary?.reduce((best, m) =>
        m.yes_prob > best.yes_prob ? m : best
    );
    if (!lead) return null;
    return { text: lead.yes_outcome || 'Yes', prob: lead.yes_prob };
}

function fmtShortDate(d: string): string {
    const dt = new Date(d);
    const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}`;
}

const TIER_CONTENT_H = { compact: 12, normal: 28, expanded: 48, large: 90, full: 140 };

export interface GanttChartProps {
    events: EventFromAsset[];
    timeDomain: [Date, Date];
    hoveredEvent?: EventFromAsset | null;
    onHover?: (e: EventFromAsset | null) => void;
    onClick?: (e: EventFromAsset) => void;
    selectedEvent?: EventFromAsset | null;
    sourceEventId?: string | null;
    chartMarginLeft?: number;
    onZoomRangeChange?: (range: [Date, Date] | null) => void;
    height?: number;
    onLaneClick?: (key: MarketType) => void;
    futureOverhangPx?: number;
}

export function GanttChart({
    events, timeDomain, hoveredEvent, onHover, onClick,
    selectedEvent, sourceEventId, chartMarginLeft, onZoomRangeChange, height: heightProp,
    onLaneClick, futureOverhangPx = 0,
}: GanttChartProps) {
    const theme = useTheme();
    const { ref: containerRef, width } = useContainerWidth();
    const [measuredHeight, setMeasuredHeight] = useState(0);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => setMeasuredHeight(entry.contentRect.height));
        ro.observe(el);
        setMeasuredHeight(el.clientHeight);
        return () => ro.disconnect();
    }, []);

    const chartHeight = heightProp ?? (measuredHeight > 50 ? measuredHeight : DEFAULT_CHART_HEIGHT);
    const svgRef = useRef<SVGSVGElement>(null);
    const [tooltipEvent, setTooltipEvent] = useState<EventFromAsset | null>(null);
    const tooltipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hoveredEventRef = useRef<EventFromAsset | null>(null);

    useEffect(() => () => { if (tooltipTimer.current) clearTimeout(tooltipTimer.current); }, []);

    const ml = chartMarginLeft ?? 100;

    const xScale = useMemo(() => {
        const chartW = width - ml - MARGIN_RIGHT;
        if (chartW <= 0) return null;
        const totalW = chartW + futureOverhangPx;
        const span = timeDomain[1].getTime() - timeDomain[0].getTime();
        const futureMs = span * futureOverhangPx / chartW;
        const displayEnd = new Date(timeDomain[1].getTime() + futureMs);
        return d3.scaleTime().domain([timeDomain[0], displayEnd]).range([ml, ml + totalW]);
    }, [width, timeDomain, ml, futureOverhangPx]);

    const visibleLaneConfigs = useMemo(() => {
        const types = new Set(events.map(e => e.market_type as MarketType));
        return LANE_CONFIG.filter(c => types.has(c.key));
    }, [events]);

    const laneLayout = useMemo(() => {
        if (!xScale || visibleLaneConfigs.length === 0) return [];

        const grouped = new Map<MarketType, EventFromAsset[]>();
        for (const cfg of visibleLaneConfigs) grouped.set(cfg.key, []);
        for (const e of events) { grouped.get(e.market_type as MarketType)?.push(e); }

        const laneCount = visibleLaneConfigs.length;
        const totalGaps = Math.max(0, laneCount - 1) * LANE_GAP;
        const availableHeight = chartHeight - TIMELINE_HEIGHT - totalGaps;
        const MIN_ROW_H = 14;
        const rowSlotH = MIN_ROW_H + ROW_GAP;
        const maxRows = Math.max(laneCount, Math.floor((availableHeight - laneCount * LANE_PADDING_Y * 2) / rowSlotH));

        // Phase 1: unconstrained row assignment per lane
        const unconstrained = visibleLaneConfigs.map(cfg => {
            const evts = grouped.get(cfg.key) || [];
            const placed = evts.length > 0 ? assignRows(evts, xScale) : [];
            const rowCount = placed.length > 0 ? Math.max(...placed.map(p => p.row)) + 1 : 1;
            return { config: cfg, placed, rowCount };
        });

        // Phase 2: fair row budget distribution
        const basePerLane = Math.floor(maxRows / laneCount);
        const rowBudgets = unconstrained.map(l => Math.min(l.rowCount, basePerLane));

        // Redistribution: lanes under budget donate to lanes over budget
        let surplus = 0;
        const needs: number[] = [];
        for (let i = 0; i < unconstrained.length; i++) {
            const want = unconstrained[i].rowCount;
            if (want < basePerLane) {
                surplus += basePerLane - want;
                needs.push(0);
            } else {
                needs.push(want - basePerLane);
            }
        }

        // Distribute surplus proportionally to lanes that need more
        const totalNeed = needs.reduce((s, n) => s + n, 0);
        if (surplus > 0 && totalNeed > 0) {
            let remaining = surplus;
            for (let i = 0; i < needs.length && remaining > 0; i++) {
                if (needs[i] > 0) {
                    const give = Math.min(needs[i], Math.ceil(surplus * needs[i] / totalNeed));
                    const actual = Math.min(give, remaining);
                    rowBudgets[i] += actual;
                    remaining -= actual;
                }
            }
        }

        // Phase 3: truncate placed events to fit within row budgets
        const laneData = unconstrained.map((lane, i) => {
            const maxRow = rowBudgets[i];
            const clipped = lane.placed.filter(p => p.row < maxRow);
            const rowCount = clipped.length > 0 ? Math.min(lane.rowCount, maxRow) : 1;
            return { config: lane.config, placed: clipped, rowCount };
        });

        // Phase 4: layout with height distribution
        const totalRows = laneData.reduce((s, l) => s + l.rowCount, 0);
        const layout: { config: LaneConfig; placed: PlacedEvent[]; rowCount: number; rowHeight: number; yOffset: number; laneHeight: number }[] = [];
        let yOffset = TIMELINE_HEIGHT;
        for (const lane of laneData) {
            const share = lane.rowCount / totalRows;
            const laneInner = Math.max(30, Math.floor(availableHeight * share));
            const usable = laneInner - LANE_PADDING_Y * 2;
            const rh = Math.max(MIN_ROW_H, Math.floor((usable - Math.max(0, lane.rowCount - 1) * ROW_GAP) / lane.rowCount));
            const actual = lane.rowCount * rh + Math.max(0, lane.rowCount - 1) * ROW_GAP + LANE_PADDING_Y * 2;
            layout.push({ config: lane.config, placed: lane.placed, rowCount: lane.rowCount, rowHeight: rh, yOffset, laneHeight: Math.max(30, actual) });
            yOffset += Math.max(30, actual) + LANE_GAP;
        }

        // Phase 5: distribute remaining space
        const used = layout.length > 0 ?
            (layout[layout.length - 1].yOffset + layout[layout.length - 1].laneHeight) - TIMELINE_HEIGHT : 0;
        const rem = availableHeight - used;
        if (rem > 5 && layout.length > 0) {
            const extra = Math.floor(rem / layout.length);
            let off = TIMELINE_HEIGHT;
            for (const lane of layout) {
                lane.yOffset = off;
                lane.laneHeight += extra;
                const u = lane.laneHeight - LANE_PADDING_Y * 2;
                lane.rowHeight = Math.max(MIN_ROW_H, Math.floor((u - Math.max(0, lane.rowCount - 1) * ROW_GAP) / lane.rowCount));
                off += lane.laneHeight + LANE_GAP;
            }
        }
        return layout;
    }, [xScale, events, visibleLaneConfigs, chartHeight]);

    // ── Probability sparkline data for large/full bars ──
    const sparklineTargets = useMemo(() => {
        if (!xScale) return [];
        const targets: { eventId: string; conditionId: string; startDate: Date; endDate: Date }[] = [];
        for (const lane of laneLayout) {
            if (lane.rowHeight < 40) continue; // only large/full tiers
            for (const { evt } of lane.placed) {
                const topMarket = pickTopMarkets(evt.markets_summary || [])[0];
                if (!topMarket?.condition_id) continue;
                targets.push({
                    eventId: evt.event_id,
                    conditionId: topMarket.condition_id,
                    startDate: new Date(evt.start_date),
                    endDate: new Date(evt.end_date),
                });
            }
        }
        return targets;
    }, [laneLayout, xScale]);

    const sparklineConditionIds = useMemo(
        () => [...new Set(sparklineTargets.map(t => t.conditionId))],
        [sparklineTargets],
    );

    const sparklineResolution = useMemo(() => {
        if (sparklineTargets.length === 0) return 1440;
        // find shortest event duration, ensure at least 5 points, cap at daily
        let minDays = Infinity;
        for (const t of sparklineTargets) {
            const days = (t.endDate.getTime() - t.startDate.getTime()) / 86400000;
            if (days < minDays) minDays = days;
        }
        // intervalMinutes such that we get ~5+ points for shortest event
        const idealMinutes = Math.floor((minDays * 24 * 60) / 5);
        return Math.min(1440, Math.max(60, idealMinutes));
    }, [sparklineTargets]);

    const { series: sparklineSeries } = useMultiMarketProbability(
        sparklineConditionIds,
        timeDomain[0],
        timeDomain[1],
        sparklineResolution,
    );

    const sparklineMap = useMemo(() => {
        const m = new Map<string, MarketSeriesData>();
        for (const s of sparklineSeries) m.set(s.conditionId, s);
        return m;
    }, [sparklineSeries]);

    // ── Sync external hoveredEvent to CSS class (no re-render) ──
    useEffect(() => {
        const svgEl = svgRef.current;
        if (!svgEl) return;
        // Remove previous external hover
        svgEl.querySelectorAll('.gantt-bar-ext-hover').forEach(el => el.classList.remove('gantt-bar-ext-hover'));
        if (hoveredEvent) {
            svgEl.querySelectorAll(`[data-event-id="${hoveredEvent.event_id}"]`).forEach(el => el.classList.add('gantt-bar-ext-hover'));
        }
    }, [hoveredEvent]);

    // ── Sync selectedEvent to CSS class (no re-render) ──
    useEffect(() => {
        const svgEl = svgRef.current;
        if (!svgEl) return;
        svgEl.querySelectorAll('.gantt-bar-selected').forEach(el => el.classList.remove('gantt-bar-selected'));
        if (selectedEvent) {
            svgEl.querySelectorAll(`[data-event-id="${selectedEvent.event_id}"]`).forEach(el => el.classList.add('gantt-bar-selected'));
        }
    }, [selectedEvent]);

    // ── Sync sourceEventId to CSS class (no re-render) ──
    useEffect(() => {
        const svgEl = svgRef.current;
        if (!svgEl) return;
        svgEl.querySelectorAll('.gantt-bar-source').forEach(el => el.classList.remove('gantt-bar-source'));
        if (sourceEventId) {
            svgEl.querySelectorAll(`[data-event-id="${sourceEventId}"]`).forEach(el => el.classList.add('gantt-bar-source'));
        }
    }, [sourceEventId]);

    // ── D3 render (only on layout/data changes, NOT on hover) ──
    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
        tooltipTimer.current = null;
        if (!xScale || laneLayout.length === 0) return;

        const isDark = theme.palette.mode === 'dark';
        const gridColor = isDark ? alpha('#fff', 0.06) : alpha('#000', 0.06);
        const textColor = isDark ? alpha('#fff', 0.5) : alpha('#000', 0.4);
        const totalH = laneLayout.length > 0
            ? laneLayout[laneLayout.length - 1].yOffset + laneLayout[laneLayout.length - 1].laneHeight
            : chartHeight;
        const chartW = width - ml - MARGIN_RIGHT;
        const totalW = chartW + futureOverhangPx;

        const defs = svg.append('defs');

        // ── Inject CSS hover styles into SVG ──
        svg.append('style').text(`
            .gantt-bar .bar-border { transition: stroke-opacity 0.12s ease, stroke-width 0.12s ease; }
            .gantt-bar .bar-ticks { opacity: 0; transition: opacity 0.12s ease; }

            .gantt-bar:hover .bar-border,
            .gantt-bar.gantt-bar-ext-hover .bar-border { stroke-opacity: 0.8; stroke-width: 1.5px; }
            .gantt-bar:hover .bar-ticks,
            .gantt-bar.gantt-bar-ext-hover .bar-ticks { opacity: 0.7; }

            .gantt-bar.gantt-bar-selected .bar-border { stroke-opacity: 1; stroke-width: 2px; }
            .gantt-bar.gantt-bar-selected .bar-ticks { opacity: 1; }

            .gantt-bar.gantt-bar-source .bar-bg { fill-opacity: ${isDark ? 0.4 : 0.35}; }
            .gantt-bar.gantt-bar-source .bar-border { stroke: ${theme.palette.primary.main}; stroke-opacity: 1; stroke-width: 2px; }
            .gantt-bar.gantt-bar-source .bar-ticks { opacity: 1; }
        `);

        // ── Timeline axis ──
        const xTicks = xScale.ticks(Math.min(8, Math.floor(chartW / 80)));
        const spanDays = (timeDomain[1].getTime() - timeDomain[0].getTime()) / 86400000;
        const fmt = spanDays > 365 ? d3.timeFormat('%b \'%y') : d3.timeFormat('%b %d');

        svg.append('g').selectAll('line').data(xTicks).join('line')
            .attr('x1', d => xScale(d)).attr('y1', TIMELINE_HEIGHT)
            .attr('x2', d => xScale(d)).attr('y2', totalH)
            .attr('stroke', isDark ? alpha('#fff', 0.05) : alpha('#000', 0.1)).attr('stroke-width', 0.75);
        svg.append('g').selectAll('text').data(xTicks).join('text')
            .attr('x', d => xScale(d)).attr('y', TIMELINE_HEIGHT - 5)
            .attr('text-anchor', 'middle').attr('fill', textColor)
            .attr('font-size', '10px').attr('font-family', 'system-ui, -apple-system, sans-serif')
            .text(d => fmt(d as Date));
        svg.append('line').attr('x1', ml).attr('y1', TIMELINE_HEIGHT).attr('x2', ml + totalW).attr('y2', TIMELINE_HEIGHT)
            .attr('stroke', isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08)).attr('stroke-width', 0.5);

        // ── Lane backgrounds ──
        for (const lane of laneLayout) {
            const { config, yOffset, laneHeight } = lane;
            svg.append('rect').attr('x', ml - 2).attr('y', yOffset).attr('width', totalW + 4).attr('height', laneHeight)
                .attr('rx', 4).attr('fill', alpha(config.color, isDark ? 0.03 : 0.015))
                .on('click', () => onLaneClick?.(config.key));
            svg.append('rect').attr('x', ml - 2).attr('y', yOffset).attr('width', 2.5).attr('height', laneHeight)
                .attr('rx', 1).attr('fill', alpha(config.color, 0.4));
        }

        // ── Brush ──
        if (onZoomRangeChange) {
            const brush = d3.brushX().extent([[ml, TIMELINE_HEIGHT], [ml + totalW, totalH]])
                .on('end', event => {
                    if (!event.sourceEvent || !event.selection) return;
                    const [x0, x1] = event.selection as [number, number];
                    bg.call(brush.move, null);
                    onZoomRangeChange([xScale.invert(x0), xScale.invert(x1)]);
                });
            const bg = svg.append('g').attr('class', 'gantt-brush').call(brush);
            bg.selectAll('.selection').attr('fill', alpha(theme.palette.primary.main, 0.1)).attr('stroke', alpha(theme.palette.primary.main, 0.3)).attr('stroke-width', 1);
            bg.select('.overlay').style('cursor', 'crosshair');
            bg.select('.overlay')
                .on('click', (event: PointerEvent) => {
                    if (!onLaneClick) return;
                    const [, mouseY] = d3.pointer(event);
                    for (const lane of laneLayout) {
                        if (mouseY >= lane.yOffset && mouseY <= lane.yOffset + lane.laneHeight) {
                            onLaneClick(lane.config.key);
                            break;
                        }
                    }
                });
        }

        // ── Event bars ──
        let clipIdx = 0;

        for (const lane of laneLayout) {
            const { config, placed, rowHeight, yOffset } = lane;

            for (const { evt, row } of placed) {
                const x1 = Math.max(ml, xScale(new Date(evt.start_date)));
                const x2 = Math.min(ml + totalW, xScale(new Date(evt.end_date)));
                const y = yOffset + LANE_PADDING_Y + row * (rowHeight + ROW_GAP);
                const barW = Math.max(4, x2 - x1);
                const barH = rowHeight;

                const isSel = selectedEvent?.event_id === evt.event_id;
                const isExtHov = hoveredEvent?.event_id === evt.event_id;
                const isSource = sourceEventId === evt.event_id;
                const outcome = outcomeLabel(evt);
                const prob = outcome?.prob ?? 0.5;
                const simAlpha = 0.35 + (evt.similarity || 0.5) * 0.65;
                const probIntensity = 0.6 + prob * 0.4;
                const fillOpacity = (isDark ? 0.18 : 0.14) * simAlpha * probIntensity;
                const hoverFillOpacity = isDark ? 0.28 : 0.24;
                const selFillOpacity = isDark ? 0.35 : 0.3;

                const clipId = `gc-${clipIdx++}`;
                defs.append('clipPath').attr('id', clipId).append('rect')
                    .attr('x', x1).attr('y', y).attr('width', barW).attr('height', barH).attr('rx', 4);

                const g = svg.append('g')
                    .attr('class', `gantt-bar${isSel ? ' gantt-bar-selected' : ''}${isExtHov ? ' gantt-bar-ext-hover' : ''}${isSource ? ' gantt-bar-source' : ''}`)
                    .attr('data-event-id', evt.event_id)
                    .style('--lane-color', config.color)
                    .style('cursor', 'pointer')
                    .on('mouseenter', function () {
                        onHover?.(evt);
                        hoveredEventRef.current = evt;
                        if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
                        tooltipTimer.current = setTimeout(() => {
                            if (hoveredEventRef.current?.event_id === evt.event_id) setTooltipEvent(evt);
                        }, 150);
                    })
                    .on('mouseleave', function () {
                        onHover?.(null);
                        hoveredEventRef.current = null;
                        if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
                        tooltipTimer.current = null;
                        setTooltipEvent(null);
                    })
                    .on('click', () => onClick?.(evt));

                // Bar fill — default uses fill-opacity so CSS can override
                g.append('rect').attr('class', 'bar-bg')
                    .attr('x', x1).attr('y', y).attr('width', barW).attr('height', barH).attr('rx', 4)
                    .attr('fill', config.color)
                    .attr('fill-opacity', isSel ? selFillOpacity : fillOpacity);

                const evtEndDate = new Date(evt.end_date);
                const domEnd = (xScale.domain() as [Date, Date])[1];
                const cutoffRight = evtEndDate > domEnd;
                const cutoffLeft = new Date(evt.start_date) < (xScale.domain() as [Date, Date])[0];

                if (cutoffLeft && cutoffRight) {
                    // Both edges clipped — top + bottom lines only, no radius
                    g.append('path').attr('class', 'bar-border')
                        .attr('d', `M${x1},${y + 0.5} L${x1 + barW},${y + 0.5} M${x1 + barW},${y + barH - 0.5} L${x1},${y + barH - 0.5}`)
                        .attr('fill', 'none')
                        .attr('stroke', config.color)
                        .attr('stroke-opacity', isSel ? 1 : isDark ? 0.3 : 0.25)
                        .attr('stroke-width', isSel ? 2 : 1);
                } else if (cutoffLeft) {
                    // Left edge clipped: right, top, bottom only (no left edge, no left radius)
                    const bx = x1;
                    const by = y + 0.5;
                    const bw = barW - 0.5;
                    const bh = barH - 1;
                    const r = 3.5;
                    g.append('path').attr('class', 'bar-border')
                        .attr('d', `M${bx},${by} L${bx + bw - r},${by} Q${bx + bw},${by} ${bx + bw},${by + r} L${bx + bw},${by + bh - r} Q${bx + bw},${by + bh} ${bx + bw - r},${by + bh} L${bx},${by + bh}`)
                        .attr('fill', 'none')
                        .attr('stroke', config.color)
                        .attr('stroke-opacity', isSel ? 1 : isDark ? 0.3 : 0.25)
                        .attr('stroke-width', isSel ? 2 : 1);
                } else if (cutoffRight) {
                    const bx = x1 + 0.5;
                    const by = y + 0.5;
                    const bw = barW - 0.5;
                    const bh = barH - 1;
                    const r = 3.5;
                    g.append('path').attr('class', 'bar-border')
                        .attr('d', `M${bx + r},${by} L${bx + bw},${by} M${bx + bw},${by + bh} L${bx + r},${by + bh} Q${bx},${by + bh} ${bx},${by + bh - r} L${bx},${by + r} Q${bx},${by} ${bx + r},${by}`)
                        .attr('fill', 'none')
                        .attr('stroke', config.color)
                        .attr('stroke-opacity', isSel ? 1 : isDark ? 0.3 : 0.25)
                        .attr('stroke-width', isSel ? 2 : 1);
                } else {
                    g.append('rect').attr('class', 'bar-border')
                        .attr('x', x1 + 0.5).attr('y', y + 0.5).attr('width', barW - 1).attr('height', barH - 1).attr('rx', 3.5)
                        .attr('fill', 'none')
                        .attr('stroke', config.color)
                        .attr('stroke-opacity', isSel ? 1 : isDark ? 0.3 : 0.25)
                        .attr('stroke-width', isSel ? 2 : 1);
                }

                // Start/end tick marks — hidden by default, shown on hover via CSS
                const tickH = Math.min(barH * 0.4, 10);
                const tickGroup = g.append('g').attr('class', 'bar-ticks');
                if (!cutoffLeft) {
                    tickGroup.append('line').attr('x1', x1).attr('y1', y - tickH).attr('x2', x1).attr('y2', y + barH + tickH)
                        .attr('stroke', alpha(config.color, 0.7)).attr('stroke-width', 1.5);
                }
                if (!cutoffRight) {
                    tickGroup.append('line').attr('x1', x1 + barW).attr('y1', y - tickH).attr('x2', x1 + barW).attr('y2', y + barH + tickH)
                        .attr('stroke', alpha(config.color, 0.7)).attr('stroke-width', 1.5);
                }

                const content = g.append('g').attr('clip-path', `url(#${clipId})`);

                type Tier = 'compact' | 'normal' | 'expanded' | 'large' | 'full';
                let tier: Tier;
                if (barH < 20) tier = 'compact';
                else if (barH < 40) tier = 'normal';
                else if (barH < 70) tier = 'expanded';
                else if (barH < 120) tier = 'large';
                else tier = 'full';

                const contentH = TIER_CONTENT_H[tier];
                const yOff = Math.max(0, Math.floor((barH - contentH) / 2));

                // Probability sparkline underlay for large/full tiers
                if ((tier === 'expanded' || tier === 'large' || tier === 'full') && evt.markets_summary?.length > 0) {
                    const topCid = pickTopMarkets(evt.markets_summary)[0]?.condition_id;
                    const seriesData = topCid ? sparklineMap.get(topCid) : null;
                    if (seriesData && seriesData.data.length >= 2) {
                        const evtStart = new Date(evt.start_date);
                        const evtEnd = new Date(evt.end_date);
                        const now = new Date();
                        const lineEnd = evtEnd > now ? now : evtEnd;
                        const lineEndX = Math.min(x1 + barW, xScale(lineEnd));

                        const pts = seriesData.data.filter(
                            p => p.t >= evtStart && p.t <= lineEnd
                        );
                        // Extend sparkline to fill bar edges
                        if (pts.length >= 1) {
                            const first = pts[0];
                            if (first.t > evtStart) {
                                pts.unshift({ ...first, t: evtStart, close: first.open, open: first.open, high: first.open, low: first.open, vol: 0 });
                            }
                            const last = pts[pts.length - 1];
                            if (last.t < lineEnd) {
                                pts.push({ ...last, t: lineEnd, vol: 0 });
                            }
                        }
                        if (pts.length >= 2) {
                            const probY = d3.scaleLinear().domain([0, 1]).range([y + barH, y]);
                            const area = d3.area<typeof pts[0]>()
                                .x(p => Math.max(x1, Math.min(lineEndX, xScale(p.t))))
                                .y0(y + barH)
                                .y1(p => probY(p.close))
                                .curve(d3.curveMonotoneX);
                            const line = d3.line<typeof pts[0]>()
                                .x(p => Math.max(x1, Math.min(lineEndX, xScale(p.t))))
                                .y(p => probY(p.close))
                                .curve(d3.curveMonotoneX);

                            g.insert('path', '.bar-border')
                                .attr('d', area(pts))
                                .attr('fill', config.color)
                                .attr('fill-opacity', isDark ? 0.1 : 0.08)
                                .attr('clip-path', `url(#${clipId})`)
                                .style('pointer-events', 'none');

                            g.insert('path', '.bar-border')
                                .attr('d', line(pts))
                                .attr('fill', 'none')
                                .attr('stroke', config.color)
                                .attr('stroke-opacity', isDark ? 0.4 : 0.3)
                                .attr('stroke-width', 1.2)
                                .attr('clip-path', `url(#${clipId})`)
                                .style('pointer-events', 'none');
                        }
                    }
                }

                const textPrimary = isDark ? alpha('#fff', 0.88) : alpha('#000', 0.82);
                const textSecondary = alpha(config.color, isDark ? 0.6 : 0.55);
                const textTertiary = alpha(config.color, isDark ? 0.45 : 0.4);

                // ── COMPACT (<20) ──
                // ── COMPACT (<20) ──
                if (tier === 'compact') {
                    const pad = 4;
                    const fs = Math.max(7, Math.min(barH * 0.65, 10));
                    const cw = fs * 0.52;
                    const space = barW - pad * 2;

                    if (space > 20 && outcome) {
                        const pct = (outcome.prob * 100).toFixed(0);
                        const outStr = `${outcome.text} ${pct}%`;
                        const dotW = fs * 0.8;
                        const dotGap = 3;
                        const outW = outStr.length * cw;
                        const fixedW = dotW + dotGap * 2 + outW;
                        const qMaxW = Math.max(0, space - fixedW);
                        const qMaxChars = Math.max(0, Math.floor(qMaxW / cw));

                        const dc = prob > 0.5
                            ? alpha('#10b981', 0.6 + prob * 0.4)
                            : alpha('#ef4444', 0.6 + (1 - prob) * 0.4);

                        const tx = x1 + pad;
                        const cy = y + barH / 2;

                        if (qMaxChars > 3) {
                            const qLabel = evt.event_title.length > qMaxChars
                                ? evt.event_title.slice(0, qMaxChars - 1) + '\u2026'
                                : evt.event_title;

                            const textEl = content.append('text')
                                .attr('x', tx)
                                .attr('y', cy)
                                .attr('dominant-baseline', 'central')
                                .attr('font-size', fs + 'px')
                                .style('pointer-events', 'none');

                            textEl.append('tspan')
                                .attr('fill', textPrimary)
                                .attr('font-weight', 400)
                                .text(qLabel + ' ');

                            textEl.append('tspan')
                                .attr('fill', dc)
                                .text('\u00B7 ');

                            textEl.append('tspan')
                                .attr('fill', config.color)
                                .attr('font-weight', 600)
                                .text(outStr);
                        } else {
                            const textEl = content.append('text')
                                .attr('y', cy)
                                .attr('dominant-baseline', 'central')
                                .attr('font-size', fs + 'px')
                                .style('pointer-events', 'none');

                            const dotR = Math.min(fs * 0.2, 3);
                            content.append('circle')
                                .attr('cx', tx + dotR).attr('cy', cy).attr('r', dotR)
                                .attr('fill', dc);

                            const oMaxChars = Math.max(0, Math.floor((space - dotR * 2 - dotGap) / cw));
                            const oLabel = outStr.length > oMaxChars
                                ? outStr.slice(0, Math.max(0, oMaxChars - 1)) + '\u2026'
                                : outStr;

                            textEl.append('tspan')
                                .attr('x', tx + dotR * 2 + dotGap)
                                .attr('fill', config.color)
                                .attr('font-weight', 600)
                                .text(oLabel);
                        }
                    } else if (space > 8 && outcome) {
                        const dotR = Math.min(barH * 0.25, 4);
                        const dc = prob > 0.5 ? alpha('#10b981', 0.6 + prob * 0.4) : alpha('#ef4444', 0.6 + (1 - prob) * 0.4);
                        content.append('circle').attr('cx', x1 + 4 + dotR).attr('cy', y + barH / 2).attr('r', dotR).attr('fill', dc);
                    }
                    // ── NORMAL (20–39) ──
                } else if (tier === 'normal') {
                    const pad = 5;
                    const fs = barH >= 28 ? 11 : barH >= 22 ? 9.5 : 8;
                    const cw = fs * 0.52;
                    const imgSz = barH - 6;
                    const hasImg = !!evt.image && barW > 80 && barH >= 22;
                    const tx = hasImg ? x1 + pad + imgSz + 4 : x1 + pad;
                    const space = barW - (tx - x1) - pad;

                    if (hasImg) {
                        const imgY = y + 3;
                        content.append('image').attr('href', evt.image!).attr('x', x1 + pad).attr('y', imgY)
                            .attr('width', imgSz).attr('height', imgSz)
                            .attr('preserveAspectRatio', 'xMidYMid slice').style('clip-path', 'inset(0 round 2px)');
                    }

                    if (space > 20) {
                        const mc = Math.max(2, Math.floor(space / cw));
                        const label = evt.event_title.length > mc ? evt.event_title.slice(0, mc - 1) + '\u2026' : evt.event_title;

                        const showOutcome = outcome && space > 30;
                        const ofs = Math.max(7.5, fs - 2);
                        const lineGap = 2;
                        const blockH = showOutcome ? fs + lineGap + ofs : fs;
                        const baseY = y + Math.floor((barH - blockH) / 2);

                        content.append('text').attr('x', tx).attr('y', baseY + fs * 0.82)
                            .attr('fill', textPrimary)
                            .attr('font-size', fs + 'px').attr('font-weight', 500)
                            .style('pointer-events', 'none').text(label);

                        if (showOutcome) {
                            const pct = (outcome.prob * 100).toFixed(0);
                            const oText = `${outcome.text} ${pct}%`;
                            const omc = Math.floor(space / (ofs * 0.56));
                            const oLabel = oText.length > omc ? oText.slice(0, omc - 1) + '\u2026' : oText;
                            content.append('text').attr('x', tx).attr('y', baseY + fs + lineGap + ofs * 0.82)
                                .attr('fill', config.color)
                                .attr('font-size', ofs + 'px').attr('font-family', 'monospace').attr('font-weight', 800)
                                .style('pointer-events', 'none').text(oLabel);
                        }
                    }

                    // ── EXPANDED (40–69) ──
                } else if (tier === 'expanded') {
                    const pad = 6;
                    const imgSz = barH - 8;
                    const hasImg = !!evt.image && barW > 120;
                    const tx = hasImg ? x1 + pad + imgSz + 6 : x1 + pad;
                    const tw = barW - (tx - x1) - pad;
                    const baseY = y + yOff;

                    if (hasImg) {
                        const imgY = y + 4;
                        content.append('image').attr('href', evt.image!).attr('x', x1 + pad).attr('y', imgY)
                            .attr('width', imgSz).attr('height', imgSz)
                            .attr('preserveAspectRatio', 'xMidYMid slice').style('clip-path', 'inset(0 round 2px)');
                    }
                    if (tw > 30) {
                        const tfs = 10.5;
                        const mc = Math.floor(tw / (tfs * 0.52));
                        const title = evt.event_title.length > mc ? evt.event_title.slice(0, mc - 1) + '\u2026' : evt.event_title;
                        content.append('text').attr('x', tx).attr('y', baseY + 13)
                            .attr('fill', textPrimary).attr('font-size', tfs + 'px').attr('font-weight', 600)
                            .style('pointer-events', 'none').text(title);

                        if (outcome) {
                            const pct = (outcome.prob * 100).toFixed(0);
                            const os = `${outcome.text} ${pct}%`;
                            const omc = Math.floor(tw / (8 * 0.56));
                            const cl = os.length > omc ? os.slice(0, omc - 1) + '\u2026' : os;
                            content.append('text').attr('x', tx).attr('y', baseY + 25)
                                .attr('fill', textPrimary).attr('font-size', '9px').attr('font-family', 'monospace').attr('font-weight', 600)
                                .style('pointer-events', 'none').text(cl);
                        }

                        const vol = formatVolume(evt.volume || 0);
                        const dl = Math.max(0, Math.ceil((new Date(evt.end_date).getTime() - Date.now()) / 86400000));
                        content.append('text').attr('x', tx).attr('y', baseY + 37)
                            .attr('fill', textSecondary).attr('font-size', '9px').attr('font-family', 'monospace')
                            .style('pointer-events', 'none').text(`${vol}  \u00B7  ${dl > 0 ? dl + 'd left' : 'Ended'}`);
                    }

                    // ── LARGE (70–119) ──
                    // ── LARGE (70–119) ──
                } else if (tier === 'large') {
                    const pad = 8;
                    const imgSz = Math.min(barH - 16, 72);
                    const hasImg = !!evt.image && barW > 140;
                    const tx = hasImg ? x1 + pad + imgSz + 8 : x1 + pad;
                    const tw = barW - (tx - x1) - pad;

                    if (hasImg) {
                        const imgY = y + Math.floor((barH - imgSz) / 2);
                        content.append('image').attr('href', evt.image!).attr('x', x1 + pad).attr('y', imgY)
                            .attr('width', imgSz).attr('height', imgSz)
                            .attr('preserveAspectRatio', 'xMidYMid slice').style('clip-path', 'inset(0 round 4px)');
                    }
                    if (tw > 30) {
                        const tfs = 12;
                        const mc = Math.floor(tw / (tfs * 0.52));
                        const title = evt.event_title.length > mc ? evt.event_title.slice(0, mc - 1) + '\u2026' : evt.event_title;

                        const vol = formatVolume(evt.volume || 0);
                        const dl = Math.max(0, Math.ceil((new Date(evt.end_date).getTime() - Date.now()) / 86400000));

                        const markets = pickTopMarkets(evt.markets_summary || []);
                        const mmc = Math.floor(tw / (8.5 * 0.52));
                        const maxMarkets = Math.floor((barH - 54 - LANE_PADDING_Y * 2) / 13);
                        const shownMarkets = markets.slice(0, Math.max(0, maxMarkets));
                        const hasTags = evt.tags.length > 0 && shownMarkets.length < markets.length;

                        // total content height: title(12) + date(14) + stats(14) + markets(13 each) + optional tags(13)
                        const totalContentH = 12 + 14 + 14 + shownMarkets.length * 13 + (hasTags ? 13 : 0);
                        const usableH = barH - LANE_PADDING_Y * 2;
                        const textTop = y + (totalContentH < usableH
                            ? Math.floor((barH - totalContentH) / 2)
                            : LANE_PADDING_Y + 4);

                        let cy = textTop;
                        content.append('text').attr('x', tx).attr('y', cy + 12)
                            .attr('fill', textPrimary).attr('font-size', tfs + 'px').attr('font-weight', 700)
                            .style('pointer-events', 'none').text(title);
                        cy += 12;

                        content.append('text').attr('x', tx).attr('y', cy + 14)
                            .attr('fill', textTertiary).attr('font-size', '9px').attr('font-family', 'monospace')
                            .style('pointer-events', 'none')
                            .text(`${fmtShortDate(evt.start_date)} \u2013 ${fmtShortDate(evt.end_date)}`);
                        cy += 14;

                        content.append('text').attr('x', tx).attr('y', cy + 14)
                            .attr('fill', textSecondary).attr('font-size', '9.5px').attr('font-family', 'monospace')
                            .style('pointer-events', 'none')
                            .text(`${evt.market_count} mkt${evt.market_count !== 1 ? 's' : ''}  \u00B7  ${vol}  \u00B7  ${dl > 0 ? dl + 'd' : 'Ended'}`);
                        cy += 14;

                        for (const m of shownMarkets) {
                            cy += 13;
                            const pct = (m.yes_prob * 100).toFixed(0);
                            const mLabel = m.yes_outcome || m.question;
                            const mText = mLabel.length > mmc - 5 ? mLabel.slice(0, mmc - 6) + '\u2026' : mLabel;
                            const isSparklineMarket = m === shownMarkets[0];
                            content.append('text').attr('x', tx).attr('y', cy)
                                .attr('fill', isSparklineMarket ? textPrimary : textSecondary)
                                .attr('font-size', isSparklineMarket ? '10px' : '8.5px')
                                .attr('font-weight', isSparklineMarket ? 600 : 400)
                                .style('pointer-events', 'none').text(`${mText}  ${pct}%`);
                        }

                        if (hasTags) {
                            cy += 13;
                            content.append('text').attr('x', tx).attr('y', cy)
                                .attr('fill', textTertiary).attr('font-size', '8px').attr('font-style', 'italic')
                                .style('pointer-events', 'none').text(evt.tags.slice(0, 3).join(' \u00B7 '));
                        }
                    }

                    // ── FULL (≥120) ──
                    // ── FULL (≥120) ──
                } else {
                    const pad = 10;
                    const imgSz = Math.min(barH - 24, 100);
                    const hasImg = !!evt.image && barW > 160;
                    const tx = hasImg ? x1 + pad + imgSz + 10 : x1 + pad;
                    const tw = barW - (tx - x1) - pad;

                    if (hasImg) {
                        const imgY = y + Math.floor((barH - imgSz) / 2);
                        content.append('image').attr('href', evt.image!).attr('x', x1 + pad).attr('y', imgY)
                            .attr('width', imgSz).attr('height', imgSz)
                            .attr('preserveAspectRatio', 'xMidYMid slice').style('clip-path', 'inset(0 round 5px)');
                    }
                    if (tw > 40) {
                        const tfs = 13;
                        const mc = Math.floor(tw / (tfs * 0.52));
                        const title = evt.event_title.length > mc * 2
                            ? evt.event_title.slice(0, mc * 2 - 1) + '\u2026' : evt.event_title;
                        const line1 = title.length > mc ? title.slice(0, mc) : title;
                        const line2 = title.length > mc ? title.slice(mc) : '';
                        const titleH = line2 ? 30 : 14;

                        const vol = formatVolume(evt.volume || 0);
                        const dl = Math.max(0, Math.ceil((new Date(evt.end_date).getTime() - Date.now()) / 86400000));

                        const markets = pickTopMarkets(evt.markets_summary || []);
                        const mmc = Math.floor(tw / (9 * 0.52));
                        // each market row = label(~12) + prob bar gap(6) = 18
                        const marketRowH = 18;
                        const headerH = titleH + 16 + 16; // title + date + stats
                        const maxMarkets = Math.floor((barH - headerH - LANE_PADDING_Y * 2 - 20) / marketRowH);
                        const shownMarkets = markets.slice(0, Math.max(0, maxMarkets));

                        const hasExtras = (evt.tags.length > 0 || evt.related_tickers.length > 0) && shownMarkets.length < markets.length;
                        const totalContentH = headerH + shownMarkets.length * marketRowH + (hasExtras ? 14 : 0);
                        const usableH = barH - LANE_PADDING_Y * 2;
                        const textTop = y + (totalContentH < usableH
                            ? Math.floor((barH - totalContentH) / 2)
                            : LANE_PADDING_Y + 4);

                        let cy = textTop;
                        content.append('text').attr('x', tx).attr('y', cy + 14)
                            .attr('fill', textPrimary).attr('font-size', tfs + 'px').attr('font-weight', 700)
                            .style('pointer-events', 'none').text(line1);
                        if (line2) {
                            content.append('text').attr('x', tx).attr('y', cy + 30)
                                .attr('fill', textPrimary).attr('font-size', tfs + 'px').attr('font-weight', 700)
                                .style('pointer-events', 'none').text(line2);
                        }
                        cy += titleH;

                        cy += 16;
                        content.append('text').attr('x', tx).attr('y', cy)
                            .attr('fill', textTertiary).attr('font-size', '10px').attr('font-family', 'monospace')
                            .style('pointer-events', 'none')
                            .text(`${fmtShortDate(evt.start_date)} \u2013 ${fmtShortDate(evt.end_date)}  \u00B7  ${evt.market_type}`);

                        cy += 16;
                        content.append('text').attr('x', tx).attr('y', cy)
                            .attr('fill', textSecondary).attr('font-size', '10px').attr('font-family', 'monospace')
                            .style('pointer-events', 'none')
                            .text(`${evt.market_count} mkt${evt.market_count !== 1 ? 's' : ''}  \u00B7  ${vol}  \u00B7  ${dl > 0 ? dl + 'd left' : 'Ended'}`);

                        for (const m of shownMarkets) {
                            cy += marketRowH;
                            const pct = (m.yes_prob * 100).toFixed(0);
                            const mLabel = m.yes_outcome || m.question;
                            const mText = mLabel.length > mmc - 5 ? mLabel.slice(0, mmc - 6) + '\u2026' : mLabel;

                            const isSparklineMarket = m === shownMarkets[0];
                            content.append('text').attr('x', tx).attr('y', cy)
                                .attr('fill', isSparklineMarket ? textPrimary : (isDark ? alpha('#fff', 0.7) : alpha('#000', 0.6)))
                                .attr('font-size', '9.5px')
                                .attr('font-weight', isSparklineMarket ? 600 : 400)
                                .style('pointer-events', 'none').text(mText);

                            const pbX = tx;
                            const pbW = Math.min(tw - 40, 80);
                            if (pbW > 15) {
                                content.append('rect').attr('x', pbX).attr('y', cy + 5).attr('width', pbW).attr('height', 3).attr('rx', 1.5)
                                    .attr('fill', alpha(config.color, 0.1));
                                content.append('rect').attr('x', pbX).attr('y', cy + 5).attr('width', pbW * m.yes_prob).attr('height', 3).attr('rx', 1.5)
                                    .attr('fill', alpha(config.color, 0.45));
                                content.append('text').attr('x', pbX + pbW + 4).attr('y', cy + 12)
                                    .attr('fill', isSparklineMarket ? textPrimary : textSecondary)
                                    .attr('font-size', '12px').attr('font-family', 'monospace')
                                    .attr('font-weight', isSparklineMarket ? 600 : 400)
                                    .style('pointer-events', 'none').text(`${pct}%`);
                            }
                        }

                        if (hasExtras) {
                            cy += 14;
                            const extras: string[] = [];
                            if (evt.tags.length > 0) extras.push(evt.tags.slice(0, 4).join(' \u00B7 '));
                            if (evt.related_tickers.length > 0) extras.push(evt.related_tickers.slice(0, 3).join(', '));
                            if (extras.length > 0) {
                                content.append('text').attr('x', tx).attr('y', cy)
                                    .attr('fill', textTertiary).attr('font-size', '8.5px').attr('font-style', 'italic')
                                    .style('pointer-events', 'none').text(extras.join('  \u2022  '));
                            }
                        }
                    }
                }

            }
        }

        // "Today" marker
        const now = new Date();
        const [domStart, domEnd] = xScale.domain() as [Date, Date];
        if (now >= domStart && now <= domEnd) {
            const tx = xScale(now);
            svg.append('line').attr('x1', tx).attr('y1', TIMELINE_HEIGHT).attr('x2', tx).attr('y2', totalH)
                .attr('stroke', isDark ? alpha('#fff', 0.40) : alpha('#000', 0.3)).attr('stroke-width', 1.5).attr('stroke-dasharray', '3 2');
            svg.append('text').attr('x', tx).attr('y', TIMELINE_HEIGHT - 5).attr('text-anchor', 'middle')
                .attr('fill', theme.palette.primary.main).attr('font-size', '9px').attr('font-weight', 600).text('Today');
        }


        // Re-apply selected/ext-hover/source classes after render
        if (selectedEvent) {
            svg.selectAll(`[data-event-id="${selectedEvent.event_id}"]`).classed('gantt-bar-selected', true);
        }
        if (hoveredEvent) {
            svg.selectAll(`[data-event-id="${hoveredEvent.event_id}"]`).classed('gantt-bar-ext-hover', true);
        }
        if (sourceEventId) {
            svg.selectAll(`[data-event-id="${sourceEventId}"]`).classed('gantt-bar-source', true);
        }
    }, [
        xScale, laneLayout, width, theme,
        // NOT hoveredEvent or sourceEventId — these are CSS-driven
        selectedEvent,
        ml, onZoomRangeChange, timeDomain,
        sparklineMap,
    ]);

    return (
        <Box ref={containerRef} sx={{ px: 1, py: 1, position: 'relative', width: '100%', height: '100%' }}>
            {laneLayout.map(lane => {
                const Icon = MUI_ICONS[lane.config.key];
                return (
                    <Tooltip key={lane.config.key} title={lane.config.label} placement="right">
                        <Box sx={{
                            position: 'absolute', left: 8, top: `${lane.yOffset + 8}px`,
                            height: lane.laneHeight, width: ml - 8,
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            gap: '3px', cursor: 'pointer', zIndex: 2, overflow: 'hidden',
                        }}
                            onClick={() => onLaneClick?.(lane.config.key)}
                        >
                            <Icon sx={{ fontSize: 13, color: lane.config.color }} />
                            <Typography noWrap sx={{
                                fontSize: '9.5px', fontWeight: 600, color: alpha(lane.config.color, 0.8),
                                letterSpacing: '0.04em', lineHeight: 1, userSelect: 'none',
                            }}>{lane.config.shortLabel}</Typography>
                        </Box>
                    </Tooltip>
                );
            })}
            <Tooltip
                open={!!tooltipEvent}
                title={tooltipEvent ? <EventTooltipContent event={tooltipEvent} /> : ''}
                placement="right-start" followCursor
                disableHoverListener disableFocusListener disableTouchListener
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                            borderRadius: '4px', p: 1.5, maxWidth: 360, boxShadow: 1,
                        }
                    }
                }}
            >
                <svg ref={svgRef} width={width} height={chartHeight} style={{ display: 'block' }}
                    onMouseLeave={() => {
                        hoveredEventRef.current = null;
                        if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
                        tooltipTimer.current = null;
                        setTooltipEvent(null);
                        onHover?.(null);
                    }}
                />
            </Tooltip>
        </Box>
    );
}